/**
 * Shipping Protection cart add-on.
 *
 * Adds or removes the Shipping Protection product as an ordinary cart line when
 * the checkbox is toggled. Because it is a real product rather than a synthetic
 * fee, the subtotal, discount codes, taxes and checkout need no special cases.
 *
 * The refresh follows the same path as assets/cart-discount.js: ask the cart
 * request for the rendered section alongside the mutation, then morph it in.
 * Morphing rather than replacing keeps the open <dialog> and the drawer's focus
 * state intact, which a full section swap would throw away.
 *
 * This module is loaded from layout/theme.liquid. It must not be loaded from
 * the cart snippet itself: that markup is re-rendered through the Section
 * Rendering API, and script tags inside morphed markup never execute.
 */

import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/* Horizon declares `Theme` as a script level const in snippets/scripts.liquid,
   so it is reachable by name but is NOT a window property. Fall back to the
   standard AJAX endpoints (prefixed for the current market root) if it is ever
   missing, so a theme update cannot silently turn the checkbox inert. */
function cartRoute(kind) {
  const themeRoutes = (typeof Theme !== 'undefined' && Theme.routes) || {};
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  if (kind === 'add') return themeRoutes.cart_add_url || `${root}cart/add.js`;
  if (kind === 'cart') return `${themeRoutes.cart_url || `${root}cart`}.js`;
  return themeRoutes.cart_change_url || `${root}cart/change.js`;
}

class ShippingProtectionComponent extends HTMLElement {
  #pending = false;

  connectedCallback() {
    const input = this.#input;
    if (!input || input.dataset.shippingProtectionBound === 'true') return;

    input.dataset.shippingProtectionBound = 'true';
    input.addEventListener('change', this.#onChange);

    /* If the shopper ticked the box before this module finished loading, the
       change event is already gone. The rendered state (data-line-key is set
       only while protection is in the cart) says what the cart really holds,
       so replay the toggle rather than leaving a tick that bought nothing. */
    const inCart = Boolean(this.dataset.lineKey);
    if (input.checked !== inCart) {
      input.dispatchEvent(new Event('change'));
    }
  }

  get #input() {
    const input = this.querySelector('input[type="checkbox"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  get #errorElement() {
    return this.querySelector('[data-shipping-protection-error]');
  }

  #onChange = async (event) => {
    const input = event.currentTarget;
    if (!(input instanceof HTMLInputElement)) return;

    /* Guard against a second toggle arriving while the first is still in
       flight. Without it a quick double click can add two protection lines. */
    if (this.#pending) {
      input.checked = !input.checked;
      return;
    }

    const wantsProtection = input.checked;
    const sectionId = this.dataset.sectionId;
    const variantId = Number(this.dataset.variantId);
    const lineKey = this.dataset.lineKey;

    if (wantsProtection && !Number.isFinite(variantId)) return;
    if (!wantsProtection && !lineKey) return;

    this.#pending = true;
    this.setAttribute('aria-busy', 'true');
    this.#clearError();

    /* Announce the mutation the way Horizon's own cart components do, with a
       promise that settles when the server has answered. cart-icon updates the
       header count from it, and the enclosing cart-items-component morphs the
       section from the rendered HTML carried in `detail.sections`. */
    const deferred = CartLinesUpdateEvent.createPromise();
    const itemsComponent = this.closest('cart-items-component');

    try {
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: wantsProtection ? 'add' : 'remove',
          context: 'cart',
          lines: wantsProtection ? [{ id: String(variantId), quantity: 1 }] : [{ id: lineKey, quantity: 0 }],
          promise: deferred.promise,
        })
      );

      const url = cartRoute(wantsProtection ? 'add' : 'change');

      const payload = wantsProtection
        ? { items: [{ id: variantId, quantity: 1 }] }
        : { id: lineKey, quantity: 0 };

      if (sectionId) payload.sections = [sectionId];

      const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(payload) }));
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.description || data?.message || 'Shipping protection could not be updated.');
      }

      const sections = data?.sections;
      const newHtml = sectionId ? sections?.[sectionId] : null;

      /* /cart/add.js answers with the added lines only: no item_count and no
         currency, and the standard events helper throws on a cart without a
         currency. /cart/change.js returns the whole cart. Use the whole cart
         when we already have it, otherwise fetch it once so the header count
         and any other listener get real numbers. */
      let fullCart = data && data.currency ? data : null;
      if (!fullCart) {
        fullCart = await fetch(cartRoute('cart'), { headers: { Accept: 'application/json' } })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null);
      }

      let itemCount = Number(fullCart?.item_count);
      if (!Number.isFinite(itemCount) && newHtml) {
        const parsed = new DOMParser().parseFromString(newHtml, 'text/html');
        const printed = parsed.querySelector('[ref="cartItemCount"]')?.textContent;
        if (printed) itemCount = parseInt(printed, 10);
      }

      let cartForEvent = null;
      try {
        cartForEvent = fullCart ? CartLinesUpdateEvent.createCartFromAjaxResponse(fullCart) : null;
      } catch (_) {
        cartForEvent = null;
      }

      deferred.resolve({
        cart: cartForEvent,
        detail: {
          sections: sections ?? undefined,
          items: data?.items,
          itemCount: Number.isFinite(itemCount) ? itemCount : undefined,
          source: 'shipping-protection',
          didError: false,
        },
      });

      if (itemsComponent && newHtml) {
        /* cart-items-component morphs the section from the event above. */
      } else if (sectionId && newHtml) {
        sectionRenderer.abortRender(sectionId);
        await morphSection(sectionId, newHtml, {
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
        });
      } else if (sectionId) {
        /* The cart endpoints occasionally answer without the rendered section.
           Ask for it separately rather than leaving stale totals on screen. */
        await sectionRenderer.renderSection(sectionId, {
          cache: false,
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
        });
      } else {
        window.location.reload();
      }
    } catch (error) {
      deferred.reject(error);
      /* Put the checkbox back where the cart actually is, so the control never
         claims a state the server did not accept. */
      input.checked = !wantsProtection;
      this.#showError(error instanceof Error ? error.message : 'Shipping protection could not be updated.');
    } finally {
      this.#pending = false;
      this.removeAttribute('aria-busy');
    }
  };

  #showError(message) {
    const element = this.#errorElement;
    if (!(element instanceof HTMLElement)) return;
    element.textContent = message;
    element.hidden = false;
  }

  #clearError() {
    const element = this.#errorElement;
    if (!(element instanceof HTMLElement)) return;
    element.textContent = '';
    element.hidden = true;
  }
}

if (!customElements.get('shipping-protection-component')) {
  customElements.define('shipping-protection-component', ShippingProtectionComponent);
}
